// src/components/modules/dashboardStats.js
// Pure helpers: turn raw timesheet entries into the numbers the dashboard shows.
// No React, no API calls — easy to unit test.

export const HOURS_PER_DAY = 8;
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Parse "YYYY-MM-DD..." as a LOCAL date (avoids the UTC off-by-one you get from new Date("2026-10-13")).
const parseDate = (v) => {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const toBool = (v) => {
  const s = String(v ?? "").toLowerCase();
  return v === true || v === 1 || s === "1" || s === "true" || s === "billable" || s === "yes";
};

/**
 * Map the API response to { date, iso, hours, billable, project }.
 * NOTE: the field names below are guesses with fallbacks — align them with
 * whatever your timesheet entries endpoint actually returns.
 */
export function normalizeEntries(raw = []) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => {
      const date = parseDate(e.date ?? e.entry_date ?? e.Entry_date ?? e.work_date ?? e.Work_date);
      const hours = Number(e.hours ?? e.Hours ?? e.total_hours ?? e.Total_hours ?? 0);
      if (!date || !Number.isFinite(hours) || hours <= 0) return null;
      return {
        date,
        iso: toISO(date),
        hours,
        billable: toBool(e.is_billable ?? e.Is_billable ?? e.billable ?? e.Billable),
        // "Client / Project", so same-named projects of different clients stay separate
        project: [e.client_name ?? e.Client_name, e.project_name ?? e.Project_name ?? e.projectName ?? "Unassigned"]
          .map((x) => (x || "").trim())
          .filter(Boolean)
          .join(" / "),
      };
    })
    .filter(Boolean);
}

/**
 * Holidays: [{ date: "YYYY-MM-DD", name, type: "General" | "Restricted" }]
 * Day rules (same as Helpers/HolidayCalendar.cs on the server):
 *   General holiday                    → holiday  (not expected, never missed; hours → holiday hours)
 *   Saturday / Sunday                  → weekend  (not expected; hours → extra)
 *   Leave day (Defaults = 1 row)       → leave    (not expected; hours → extra)
 *   Restricted holiday, nothing logged → holiday  (their optional holiday)
 *   Restricted holiday, hours logged   → working  (normal working day)
 *   Anything else                      → working  (8 h expected; first 8 h regular, rest extra)
 * Today counts as expected only once something is logged for today.
 */
export function classifyDay(d, { loggedHours = 0, isLeave = false, holidayMap = new Map() } = {}) {
  const h = holidayMap.get(toISO(d));
  if (h && h.type === "General") return "holiday";
  if (d.getDay() === 0 || d.getDay() === 6) return "weekend";
  if (isLeave) return "leave";
  if (h && h.type === "Restricted" && !(loggedHours > 0)) return "holiday";
  return "working";
}

const normType = (t) => (String(t || "").toLowerCase().startsWith("r") ? "Restricted" : "General");

/** Accepts ["YYYY-MM-DD"] or [{ date | holiday_date, name | holiday_name, type | holiday_type }]. */
export function toHolidayMap(holidays = []) {
  const map = new Map();
  for (const h of holidays || []) {
    if (typeof h === "string") { map.set(h.slice(0, 10), { date: h.slice(0, 10), name: "Holiday", type: "General" }); continue; }
    const date = String(h?.date ?? h?.holiday_date ?? "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    map.set(date, { date, name: h.name ?? h.holiday_name ?? "Holiday", type: normType(h.type ?? h.holiday_type) });
  }
  return map;
}

/**
 * @param {Array} entries   normalized entries (see normalizeEntries)
 * @param {Object} opts     { today, asOf (any day in the month to show; default today), hoursPerDay, holidays: [{ date, name, type }], leaveDates: ["YYYY-MM-DD"],
 *                            joiningDate, exitDate: "YYYY-MM-DD" (HR dates; expected / missed only while employed) }
 */
export function buildDashboardStats(
  entries = [],
  { today = new Date(), asOf = null, hoursPerDay = HOURS_PER_DAY, holidays = [], leaveDates = [], joiningDate = "", exitDate = "" } = {}
) {
  // rt = the real today (today counts once logged, missed = before today, calendar "today" / "upcoming")
  // t  = end of the period shown: rt for the current month, the last day of a past month (MTD = that month,
  //      YTD = 1 Jan → that day)
  const rt = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let t = rt;
  if (asOf) {
    const monthEnd = new Date(asOf.getFullYear(), asOf.getMonth() + 1, 0);
    if (monthEnd < rt) t = monthEnd;
  }
  const employedFrom = parseDate(joiningDate);   // null → from 1 Jan
  const employedTo = parseDate(exitDate);        // null → still employed
  const year = t.getFullYear();
  const month = t.getMonth();
  const holidayMap = toHolidayMap(holidays);
  const leaveSet = new Set(leaveDates);

  const hoursByDay = new Map();
  const months = MONTH_LABELS.slice(0, month + 1).map((label) => ({ label, billable: 0, nonBillable: 0 }));
  const projectMap = new Map();
  let ytdB = 0, ytdN = 0, mtdB = 0, mtdN = 0;

  for (const e of entries) {
    if (e.date.getFullYear() !== year || e.date > t) continue; // this year, up to today only
    hoursByDay.set(e.iso, (hoursByDay.get(e.iso) || 0) + e.hours);

    const m = months[e.date.getMonth()];
    if (e.billable) { m.billable += e.hours; ytdB += e.hours; }
    else { m.nonBillable += e.hours; ytdN += e.hours; }

    if (e.date.getMonth() === month) {
      if (e.billable) mtdB += e.hours; else mtdN += e.hours;
      const p = projectMap.get(e.project) || { name: e.project, hours: 0, billableHours: 0 };
      p.hours += e.hours;
      if (e.billable) p.billableHours += e.hours;
      projectMap.set(e.project, p);
    }
  }

  const kindOf = (d) => classifyDay(d, {
    loggedHours: hoursByDay.get(toISO(d)) || 0,
    isLeave: leaveSet.has(toISO(d)),
    holidayMap,
  });

  // Walk Jan 1 → today: expected days, missed days, and regular / extra / holiday hours.
  let workingDaysMTD = 0, workingDaysYTD = 0, missedYTD = 0;
  let mtdRegular = 0, mtdExtra = 0, mtdHoliday = 0, ytdRegular = 0, ytdExtra = 0, ytdHoliday = 0;
  const missedMTD = [];
  const holidaysMTD = [];
  const extraDays = [];    // this year: { iso, date, logged, regular, extra, reason: weekend | leave | above_8h }
  const holidayDays = [];  // this year: { iso, date, name, type, hours }
  for (let d = new Date(year, 0, 1); d <= t; d.setDate(d.getDate() + 1)) {
    const iso = toISO(d);
    const h = hoursByDay.get(iso) || 0;
    const kind = kindOf(d);
    const inMonth = d.getMonth() === month;
    const isToday = d.getTime() === rt.getTime();

    // Hours split
    let regular = 0, extra = 0, holiday = 0;
    if (kind === "working") { regular = Math.min(h, hoursPerDay); extra = h - regular; }
    else if (kind === "holiday") holiday = h;
    else extra = h; // weekend, leave
    ytdRegular += regular; ytdExtra += extra; ytdHoliday += holiday;
    if (inMonth) { mtdRegular += regular; mtdExtra += extra; mtdHoliday += holiday; }

    if (inMonth && holidayMap.has(iso)) holidaysMTD.push({ ...holidayMap.get(iso), hours: h, taken: kind === "holiday" });
    if (extra > 0) extraDays.push({
      iso, date: new Date(d), logged: h, regular, extra,
      reason: kind === "weekend" ? "weekend" : kind === "leave" ? "leave" : "above_8h",
    });
    if (holiday > 0) {
      const hd = holidayMap.get(iso) || { name: "Holiday", type: "General" };
      holidayDays.push({ iso, date: new Date(d), name: hd.name, type: hd.type, hours: holiday });
    }

    if (kind !== "working") continue;
    if ((employedFrom && d < employedFrom) || (employedTo && d > employedTo)) continue; // not employed that day
    // Expected: today only once something is logged for it
    if (!isToday || h > 0) { workingDaysYTD++; if (inMonth) workingDaysMTD++; }
    // Missed: a working day before today with nothing logged
    if (!isToday && !h) { missedYTD++; if (inMonth) missedMTD.push(new Date(d)); }
  }

  // Calendar cells, Monday-first grid.
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const missedSet = new Set(missedMTD.map(toISO));
  const cells = Array.from({ length: offset }, (_, i) => ({ key: `blank-${i}`, status: "blank" }));
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    const iso = toISO(d);
    const hol = holidayMap.get(iso);
    let status = "logged";
    if (hol && hol.type === "General") status = "holiday";
    else if (d.getDay() === 0 || d.getDay() === 6) status = "off";
    else if (leaveSet.has(iso)) status = "leave";
    else if (hol && hol.type === "Restricted" && !(hoursByDay.get(iso) > 0)) status = "restricted"; // optional holiday, not worked
    else if (d.getTime() === rt.getTime()) status = "today";
    else if (d > rt) status = "future";
    else if (missedSet.has(iso)) status = "missed";
    else if ((employedFrom && d < employedFrom) || (employedTo && d > employedTo)) status = "off"; // not employed
    cells.push({ key: iso, iso, day, status, hours: hoursByDay.get(iso) || 0, holiday: hol || null });
  }

  // Month close = last company working day of the month (weekdays − General holidays).
  const isCompanyWorkingDay = (d) => classifyDay(d, { loggedHours: 1, holidayMap }) === "working";
  const monthClose = new Date(year, month, daysInMonth);
  while (!isCompanyWorkingDay(monthClose) && monthClose.getDate() > 1) monthClose.setDate(monthClose.getDate() - 1);

  const mtdLogged = mtdB + mtdN;
  const expected = workingDaysMTD * hoursPerDay;
  const ytdTotal = ytdB + ytdN;
  const ytdExpected = workingDaysYTD * hoursPerDay;

  return {
    mtd: {
      billable: mtdB,
      nonBillable: mtdN,
      logged: mtdLogged,                                   // all hours, incl. weekends and > 8 h days
      expected,                                            // working days × 8 h (holidays excluded)
      regular: mtdRegular,                                 // hours inside the expected 8 h on working days
      extra: mtdExtra,                                     // weekend / leave-day hours + hours above 8 h a day
      holiday: mtdHoliday,                                 // hours logged on holidays (own figure, not extra)
      notLogged: Math.max(expected - mtdRegular, 0),       // shortfall on working days (extra hours don't fill gaps)
      workingDays: workingDaysMTD,
      billablePct: mtdLogged ? (mtdB / mtdLogged) * 100 : 0,
      utilization: expected ? (mtdLogged / expected) * 100 : 0, // logged ÷ expected — can go above 100%
    },
    ytd: {
      billable: ytdB,
      nonBillable: ytdN,
      logged: ytdTotal,
      expected: ytdExpected,
      regular: ytdRegular,
      extra: ytdExtra,
      holiday: ytdHoliday,
      billablePct: ytdTotal ? (ytdB / ytdTotal) * 100 : 0,
      utilization: ytdExpected ? (ytdTotal / ytdExpected) * 100 : 0,
      missedDays: missedYTD,
    },
    missedDays: missedMTD.map((d) => ({
      iso: toISO(d),
      date: d,
      daysOverdue: Math.round((rt - d) / 86400000), // round handles DST shifts
    })),
    holidays: holidaysMTD,
    extraDays: extraDays.reverse(),     // newest first
    holidayDays: holidayDays.reverse(), // newest first                                 // this month's holidays: { date, name, type, hours, taken }
    projects: [...projectMap.values()].sort((a, b) => b.hours - a.hours),
    months,
    cells,
    monthClose,
  };
}